/**
 * "Extract From Documents": pick the company's engagement letter and
 * guidelines, and the assistant proposes the standing instructions in them.
 * They arrive as suggestions on the Playbook tab; nothing goes live until an
 * admin accepts it.
 */
import { FileText, Loader2, Upload } from "lucide-react";
import { useRef, useState, type FC } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

import { guidelineActions } from "../../timeTracking/actions";
import { useGuidelines } from "../../timeTracking/queries";
import {
  fileProblem,
  GUIDELINE_ACCEPT
} from "../../timeTracking/rules/ruleModel";
import { playbookActions } from "../actions";
import type { Company } from "../types";

const KIND_LABEL = {
  engagement_letter: "Engagement letter",
  guidelines: "Billing guidelines"
} as const;

export const ExtractPlaybookDialog: FC<{ company: Company }> = ({
  company
}) => {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const docs = useGuidelines({ company_id: company.id }, undefined, open);
  const rows = (docs.data ?? []).filter((d) => d.status !== "failed");
  const chosen = picked ?? rows.map((d) => d.id);

  const upload = async (file: File | undefined) => {
    if (!file) return;
    const problem = fileProblem(file);
    if (problem) {
      toast.error(problem);
      return;
    }
    const saved = await guidelineActions.upload(
      { company_id: company.id },
      file,
      "engagement_letter"
    );
    if (saved && picked) setPicked([...picked, saved.id]);
  };

  const run = async () => {
    setBusy(true);
    const out = await playbookActions.extract(company, chosen);
    setBusy(false);
    if (out) setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setPicked(null);
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost">
          <FileText /> Extract From Documents
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-120" data-testid="extract-playbook">
        <DialogHeader>
          <DialogTitle>Extract From Documents</DialogTitle>
          <DialogDescription>
            Reads {company.name}&apos;s engagement letter and guidelines and
            suggests the instructions in them. You review each one before agents
            follow it.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          {docs.isLoading ? <Skeleton className="h-16" /> : null}
          {!docs.isLoading && !rows.length ? (
            <span className="rounded-md border border-dashed border-line-2 px-3 py-3 text-base text-fg-3">
              No documents on file for {company.name}. Upload the engagement
              letter, or add the billing guidelines on the Billing tab.
            </span>
          ) : null}
          {rows.map((d) => (
            <div key={d.id} className="flex items-center gap-2.5 text-base">
              <Checkbox
                id={`extract-${d.id}`}
                checked={chosen.includes(d.id)}
                onCheckedChange={(on) =>
                  setPicked(
                    on ? [...chosen, d.id] : chosen.filter((id) => id !== d.id)
                  )
                }
              />
              <Label
                htmlFor={`extract-${d.id}`}
                className="flex min-w-0 flex-1 flex-col items-start gap-0 font-normal"
              >
                <span className="truncate font-medium">{d.filename}</span>
                <span className="text-sm text-fg-3">{KIND_LABEL[d.kind]}</span>
              </Label>
            </div>
          ))}
          <input
            ref={input}
            type="file"
            accept={GUIDELINE_ACCEPT}
            className="hidden"
            aria-label="Engagement letter file"
            onChange={(e) => {
              void upload(e.currentTarget.files?.[0]);
              e.currentTarget.value = "";
            }}
          />
          <Button
            size="sm"
            variant="secondary"
            className="w-fit"
            onClick={() => input.current?.click()}
          >
            <Upload /> Upload engagement letter
          </Button>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!chosen.length || busy}
            onClick={() => void run()}
          >
            {busy ? <Loader2 className="animate-spin" /> : null}
            {chosen.length === 0
              ? "Read documents"
              : chosen.length === 1
              ? "Read 1 document"
              : `Read ${chosen.length} documents`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
